import React from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import { dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';
import useStyles from './newer-offer-modal.styles';

const NewerOfferModal = observer(({ open }) => {
  const classes = useStyles();
  const history = useHistory();
  const { offer: offerStore } = useStore();

  const { newerOfferAvailable, dismissNewerOfferModal, offer, setNavigatingInternally } = offerStore;

  if (!newerOfferAvailable) {
    return null;
  }

  const formattedTimestamp = dateTimeFormatterInUserTimezone(newerOfferAvailable.updatedDateTime);

  // Format policy type for display
  const formatPolicyType = (policyType) => {
    if (!policyType) {
      return 'insurance';
    }

    // Convert policy type to user-friendly format (e.g., "auto" -> "Auto", "haBundle" -> "Auto + Home")
    const typeMap = {
      auto: 'Auto',
      home: 'Home',
      renters: 'Renters',
      condo: 'Condo',
      haBundle: 'Auto + Home',
      arBundle: 'Auto + Renters',
      caBundle: 'Condo + Auto'
    };
    return typeMap[policyType] || policyType;
  };

  // Get staff username for analytics (from session storage)
  const currentStaffUsername = sessionStorage.getItem('branchUsername') || 'unknown';

  // Format updated by username for display
  const updatedByDisplay = newerOfferAvailable.updatedBy || 'an agent';
  const policyTypeDisplay = formatPolicyType(newerOfferAvailable.policyType);

  const handleStay = () => {
    track('Newer Offer Modal - Stay Clicked', {
      staffUsername: currentStaffUsername,
      currentOfferId: offer?.id,
      currentOfferWebUserId: offer?.webUserId,
      newerOfferId: newerOfferAvailable.id,
      newerOfferUpdatedDateTime: newerOfferAvailable.updatedDateTime,
      policyType: newerOfferAvailable.policyType,
      newerOfferUpdatedBy: newerOfferAvailable.updatedBy
    });
    dismissNewerOfferModal();
  };

  const handleSwitch = () => {
    track('Newer Offer Modal - Switch Clicked', {
      staffUsername: currentStaffUsername,
      currentOfferId: offer?.id,
      currentOfferWebUserId: offer?.webUserId,
      newerOfferId: newerOfferAvailable.id,
      newerOfferUpdatedDateTime: newerOfferAvailable.updatedDateTime,
      policyType: newerOfferAvailable.policyType,
      newerOfferUpdatedBy: newerOfferAvailable.updatedBy
    });
    dismissNewerOfferModal();
    // Set flag to prevent modal from showing again when navigating internally
    setNavigatingInternally(true);
    history.push(`/offer/${newerOfferAvailable.id}`);
  };

  return (
    <BaseDialog open={open} onClose={handleStay} className={classes.modal} size="md">
      <div className={classes.container}>
        <Label type="greenBig" className={classes.title}>
          An updated version of this offer is available
        </Label>
        <Label type="modalBody" className={classes.body}>
          Agent {updatedByDisplay} updated a version of this applicant's {policyTypeDisplay} offer on{' '}
          {formattedTimestamp}. Would you like to open that one instead?
        </Label>
        <div className={classes.actions}>
          <Button className={classes.stay} onClick={handleStay} variant="contained" color="primary">
            Stay Here
          </Button>
          <Button className={classes.switch} onClick={handleSwitch} variant="contained" color="secondary">
            Open Other Offer
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
});

export default NewerOfferModal;
